import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { AssetTagModel } from '../../../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../../../services/axios/skymap-axios-service-factory';
import { getSelectedProjectId } from '../../../../../routing/utils';
import { ProjectContext } from '../../../../../state/project-state/project-state';
import { Button } from '../../../../button/button';
import { Dialog } from '../../../../dialog/dialog';
import { DropdownSuggestion } from '../../../../dropdown-suggestion/dropdown-suggesion';
import { InfoBox } from '../../../../info-box/info-box';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Stack } from '../../../../stack/stack';

type Props = {
  onClose: (addedTag?: AssetTagModel) => void;
  excludeTagIds?: string[];
};

enum Status {
  Idle,
  Saving,
  Error,
}

interface SelectedAssetTag {
  id: string;
  name: string;
}

const AssetTagDialog = (props: Props) => {
  const { t } = useTranslation();
  const [status, setStatus] = React.useState(Status.Idle);
  const [assetTag, setAssetTag] = React.useState<SelectedAssetTag | undefined>(undefined);
  const { project, reloadAssetTags } = React.useContext(ProjectContext);

  const close = (tag?: AssetTagModel) => props.onClose(tag);

  const onSave = async () => {
    try {
      setStatus(Status.Saving);

      const inputEl = document.getElementById('assetTagName') as HTMLInputElement | null;
      const typedName = (assetTag?.name ?? inputEl?.value ?? '').trim();
      if (!typedName) {
        setStatus(Status.Idle);
        return;
      }

      if (assetTag?.id) {
        const existingById = (project.assetTags ?? []).find((x) => x.id === assetTag.id);
        if (existingById) {
          setStatus(Status.Idle);
          close(existingById);
          return;
        }
      }

      const existingByName = (project.assetTags ?? []).find(
        (x) => x.name.trim().toLowerCase() === typedName.toLowerCase(),
      );
      if (existingByName) {
        setStatus(Status.Idle);
        close(existingByName);
        return;
      }

      const { data } = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV1()
        .createAssetTag({
          path: { projectId: getSelectedProjectId() as string },
          body: { name: typedName },
        });

      await reloadAssetTags();

      setStatus(Status.Idle);
      close(data);
    } catch (err) {
      setStatus(Status.Error);
    }
  };

  const disable = status === Status.Saving;

  return (
    <Dialog closeIcon={true} closeOnDimmerClick={true} onClose={() => close()}>
      {{
        header: t('fileManager.folderTagsDialog.title', { ns: 'components' }),
        content: (
          <Stack spacing={1}>
            {status === Status.Error && (
              <InfoBox color="red">
                <Trans
                  components={{ br: <br /> }}
                  i18nKey="fileManager.folderTagsDialog.unknownError"
                  ns="components"
                />
              </InfoBox>
            )}
            <LabelledContainer
              text={t('fileManager.folderTagsDialog.nameColumn', { ns: 'components' })}
            >
              <DropdownSuggestion
                clearOnSelection={true}
                disabled={disable}
                id="assetTagName"
                placeholder={t('fileManager.folderTagsDialog.nameColumn', { ns: 'components' })}
                portalToBody={true}
                suggestions={(project.assetTags ?? [])
                  .filter((tag) => !(props.excludeTagIds ?? []).includes(tag.id))
                  .map((tag) => ({ id: tag.id, name: tag.name }))}
                value={assetTag?.id}
                onSuggestionSelected={(selected) =>
                  setAssetTag((project.assetTags ?? []).find((tag) => tag.id === selected.id))
                }
              />
            </LabelledContainer>
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={disable}
                variant="contained"
                onClick={() => void onSave()}
              >
                {t('save', { ns: 'common' })}
              </Button>
              <Button disabled={disable} variant="text" onClick={() => close()}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

export { AssetTagDialog };
